import React, { memo } from "react";

import { faMap } from "@fortawesome/pro-solid-svg-icons/faMap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Located from "@mapmycustomers/shared/types/base/Located";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import ChangePinLocationModal from "./ChangePinLocationModal";
import styles from "./MapPopup.module.scss";

interface Props {
  changeButtonDisabled?: boolean;
  changeButtonLoading?: boolean;
  entity?: EntityWithLocation;
  onUpdate: (located: Located) => void;
  useButton?: boolean;
}

const MapPopup: React.FC<Props> = ({
  changeButtonDisabled,
  changeButtonLoading,
  entity,
  onUpdate,
  useButton,
}) => {
  const intl = useIntl();
  const [visible, show, hide] = useBoolean(false, true);
  const hasAddress = !!entity?.address || !!entity?.geoPoint?.coordinates;

  return (
    <React.Fragment>
      <Tooltip
        placement="bottom"
        title={
          changeButtonDisabled
            ? intl.formatMessage({
                id: "recordPane.mapPopup.disabledButton.tooltip",
                defaultMessage: "You must save your changes below in order to update pin location",
                description: "Edit pane / details / disabled button / tooltip",
              })
            : null
        }
      >
        {useButton ? (
          <Tooltip
            title={
              changeButtonDisabled
                ? null
                : intl.formatMessage({
                    id: "recordPane.mapPopup.previewPin",
                    defaultMessage: "Preview Pin on Map",
                    description: "Edit pane / details / preview pin on map",
                  })
            }
            trigger={hasAddress ? ["hover"] : []}
          >
            <Button
              disabled={changeButtonDisabled || !entity}
              loading={changeButtonLoading}
              onClick={show}
            >
              {hasAddress
                ? intl.formatMessage({
                    id: "recordPane.mapPopup.changePinButton",
                    defaultMessage: "Change pin location",
                    description: "Label for change pin location button on record pane",
                  })
                : intl.formatMessage({
                    id: "recordPane.mapPopup.setOnMap",
                    defaultMessage: "Set on Map",
                    description: "Label for change pin location button on record pane",
                  })}
            </Button>
          </Tooltip>
        ) : (
          <Button
            className={styles.button}
            disabled={changeButtonDisabled || !entity}
            icon={<FontAwesomeIcon icon={faMap} />}
            loading={changeButtonLoading}
            onClick={show}
            size="small"
            type="link"
          />
        )}
      </Tooltip>
      {visible && (
        <ChangePinLocationModal
          // we can't show the modal if entity is undefined
          entity={entity!}
          onHide={hide}
          onUpdate={onUpdate}
        />
      )}
    </React.Fragment>
  );
};

export default memo(MapPopup);
